import { createContext } from 'react';

import Attribute from 'types/Attribute';
import Picture from 'types/Picture';
import SetState from 'types/SetState';
import Price from 'types/Price';
import { Filter, QueryFilter } from 'types/Filter';
import CurrentSupplier from 'types/CurrentSupplier';
import OptionGroup from 'types/ProductOptionGroup';
import AttributeDescription from 'types/AttributeDescription';
import SearchItem from 'types/SearchItem';
import { UrlVariant, SimpleVariant, ProductCategoryLevels } from 'types/Variant';
import { MappedTrait } from 'types/MappedTrait';
import { Manufacturer } from 'types/models/Manufacturer';
import { ProductType } from 'types/models/ProductType';
import { ShopVariant } from 'types/models/ShopVariant';
import { Product } from 'types/models/Product';
import { Variant } from 'types/models/Variant';
import { Media } from 'types/models/Media';
import { PackageContent } from 'types/models/PackageContent';
import { Tag } from 'types/models/Tag';
import { defaultPrice } from 'consts/price';
import { SectionId } from '@pages/Product/Product.consts';

export type ProductContextType = UrlVariant &
  Required<
    Pick<
      Variant,
      | 'names'
      | 'price'
      | 'priceRrp'
      | 'producerPn'
      | 'productCode'
      | 'ean'
      | 'isEol'
      | 'availability'
      | 'rating'
      | 'ratingCount'
      | 'shipsUntil'
      | 'documents'
    >
  > &
  Pick<Variant, 'description' | 'thumbnail' | 'isLeasingAvailable'> &
  Pick<ShopVariant, 'isForSale'> & {
    isPrerelease: boolean;
    product: Required<Pick<Product, 'id' | 'name'>>;
    traits: MappedTrait[];
    video?: Required<Media>;
    attributes: Attribute[];
    pictures: Picture[];
    attributesDescriptions: AttributeDescription[];
    productType: Required<Pick<ProductType, 'id' | 'name'>>;
    selectedOptions: Record<string, string>;
    manufacturer: Manufacturer;
    optionGroups: OptionGroup[];
    variants: SearchItem[];
    constantFilters: QueryFilter[];
    filters: Filter[];
    recommendedVariants: (SimpleVariant & { product: ProductCategoryLevels })[];
    complementaryVariants: SimpleVariant[];
    packageContents: PackageContent[];
    tags: Tag[];
    currentSupplier: CurrentSupplier;
    anchorIndex: number;
    fullPrice: Price;
    grossPrice: Price;
    fullGrossPrice: Price;
    selectedComplementaryProductsIds: number[];
    sectionsVisibility?: Partial<Record<SectionId, boolean>>;
    setAnchorIndex: SetState<number>;
    setSelectedComplementaryProductsIds: SetState<number[]>;
  };

export const ProductContext = createContext<ProductContextType>({
  id: 0,
  anchorIndex: -1,
  producerPn: '',
  productCode: '',
  ean: '',
  shipsUntil: null,
  currentSupplier: { availabilityTimespan: { id: 0, name: '', days: 0, type: 'DEFAULT' } },
  availability: 0,
  priceRrp: defaultPrice,
  price: defaultPrice,
  fullPrice: defaultPrice,
  grossPrice: defaultPrice,
  fullGrossPrice: defaultPrice,
  rating: null,
  ratingCount: 0,
  thumbnail: undefined,
  description: undefined,
  video: undefined,
  sectionsVisibility: undefined,
  product: { id: 0, name: '' },
  productType: { id: 0, name: '' },
  manufacturer: { id: 0, name: '' },
  selectedOptions: {},
  packageContents: [],
  traits: [],
  tags: [],
  optionGroups: [],
  names: [],
  pictures: [],
  attributes: [],
  attributesDescriptions: [],
  documents: [],
  variants: [],
  constantFilters: [],
  filters: [],
  recommendedVariants: [],
  complementaryVariants: [],
  selectedComplementaryProductsIds: [],
  isEol: false,
  isForSale: false,
  isPrerelease: false,
  setAnchorIndex: () => {},
  setSelectedComplementaryProductsIds: () => {},
});
